import { useEffect, useMemo } from 'react'
import { Button, Divider, Typography } from '@mui/material'
import { ResAddMembersPayload } from '@weblite/ts-action/subscription'
import { styled } from '@mui/material/styles'
import { AlertCircleOutlinedIcon } from '@weblite/icons'
import { closeCurrentModal } from 'weblite-web-modal-viewer'
import { SelectableUserListItemWithStatus } from '$common/user-list-item'
import { useMultiSelectList } from '$common/multi-select-searchable-list-page'
import { t } from '$i18n'
import { BottomDrawer } from '$common/bottom-drawer'
import { sendInviteLinkToReceivers } from '$logic/message'
import { useRoomAttribute } from '$logic/room'
import { toLocale } from '$func/text'
import { convertInviteLinkToReadableFormat } from '$logic/hall/helper'

const StyledDivider = styled(Divider)`
  &:last-child {
    display: none;
  }
`

export function SendInviteLinkDialog({
  restrictedIds = [],
  hallBannedIds = [],
  roomId,
  inviteLink,
}: ResAddMembersPayload & { roomId: string; inviteLink: string | undefined }) {
  const items = useMemo(() => {
    return restrictedIds.map(id => ({ id }))
  }, [restrictedIds])

  const isGroup = useRoomAttribute('type', roomId) === 'GROUP'

  const { selectionMap, setSelection, selectedItems, hasSelection } =
    useMultiSelectList<{
      id: string
    }>({ items, selectionPrimaryKey: 'id' })

  useEffect(() => {
    restrictedIds.forEach(id => setSelection(id, true))
  }, [restrictedIds])

  const onSend = async () => {
    sendInviteLinkToReceivers(
      selectedItems.map(({ id }) => id),
      convertInviteLinkToReadableFormat(inviteLink || ''),
      isGroup,
    )
    await closeCurrentModal()
  }

  const shouldSendInviteLink = restrictedIds.length > 0
  const shouldUnbanUsers = hallBannedIds.length > 0

  return (
    <BottomDrawer isOpen onClose={closeCurrentModal}>
      <Column style={{ padding: '16px 0', gap: '16px' }}>
        <Column style={{ padding: '0 16px', gap: '8px' }}>
          <Typography variant="subtitle1" color="secondary700">
            {t('sendInviteLink.title')}
          </Typography>
          <Typography variant="paragraph2">
            {t('sendInviteLink.description')(isGroup)}
          </Typography>
          {shouldUnbanUsers && (
            <Row>
              <AlertCircleOutlinedIcon color="error" />
              <Typography variant="body2" color="error">
                {t('sendInviteLink.shouldUnban')(
                  toLocale(hallBannedIds.length),
                )}
              </Typography>
            </Row>
          )}
        </Column>
        {shouldSendInviteLink ? (
          <>
            <Column>
              {restrictedIds.map(userId => (
                <>
                  <SelectableUserListItemWithStatus
                    key={userId}
                    userId={userId}
                    selected={selectionMap[userId]}
                    onSelectedChange={isSelected =>
                      setSelection(userId, isSelected)
                    }
                    hasDivider={false}
                  />
                  <StyledDivider />
                </>
              ))}
            </Column>
            <Column style={{ padding: '0 16px' }}>
              <Button
                onClick={onSend}
                fullWidth
                disabled={!hasSelection}
                sx={{
                  backgroundColor: 'secondary500',
                  color: 'neutral900',
                  '&:hover': {
                    backgroundColor: 'secondary500',
                  },
                }}
              >
                {t('sendInviteLink.title')}
              </Button>
            </Column>
          </>
        ) : null}
      </Column>
    </BottomDrawer>
  )
}

const Column = styled('div')`
  display: flex;
  flex-direction: column;
`

const Row = styled('div')`
  display: flex;
  gap: 5px;
`
